import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import CinemaBrand from "@/components/CinemaBrand";
import { DisplayMusicByToken } from "@/components/DisplayMusic";
import { eventType } from "@/lib/constants";
import { Clock, MapPin, Ticket, Calendar as CalendarIcon } from "lucide-react";

/**
 * Vorschaubildschirm — Große Poster-Karussell-Slides mit Kinodetails.
 * Zeigt kommende Vorstellungen der nächsten 7 Tage. Wechselt alle 12 s.
 */
export default function ProgramPreview() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const load = () => api.get(`/displays/public/${token}`).then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60_000);
    const c = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(t); clearInterval(c); };
  }, [token]);

  const shows = useMemo(() => (data?.upcoming || []).filter((s) => s.movie), [data]);

  useEffect(() => {
    if (shows.length <= 1) return;
    const r = setInterval(() => setIdx((i) => (i + 1) % shows.length), 12_000);
    return () => clearInterval(r);
  }, [shows.length]);

  if (!data) return <div className="min-h-screen bg-black flex items-center justify-center text-[#A8A398]">Lade Vorschau …</div>;

  if (!shows.length) {
    return (
      <div className="min-h-screen w-full bg-black text-[#F5F0E6] flex flex-col items-center justify-center p-10">
        <CinemaBrand size="xl" stacked />
        <div className="mt-10 text-[#A8A398] text-lg font-heading">Keine Vorstellungen in den nächsten 7 Tagen.</div>
      </div>
    );
  }

  const s = shows[idx % shows.length];
  const m = s.movie;
  const ev = eventType(s.event_type);
  const bg = s.wallpaper_url || m.backdrop_url || m.poster_url;

  const dateLabel = fmtDate(s.date);
  const daysAway = Math.max(0, Math.round((new Date(s.date + "T00:00:00") - new Date(now.toDateString())) / 86400000));

  return (
    <div className="min-h-screen w-full bg-black text-[#F5F0E6] relative overflow-hidden">
      <DisplayMusicByToken token={token} autoStart hideToggle />
      {/* Backdrop */}
      {bg && (
        <>
          <img
            key={s.id + "-bg"}
            src={bg}
            alt=""
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            className="absolute inset-0 w-full h-full object-cover opacity-40 animate-slow-zoom"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50" />
        </>
      )}
      {/* Velvet grain */}
      <div className="absolute inset-0 opacity-[0.04] mix-blend-overlay"
        style={{ backgroundImage: "radial-gradient(#D4AF37 1px, transparent 1px)", backgroundSize: "3px 3px" }} />

      <div className="relative z-10 min-h-screen grid grid-rows-[auto_1fr_auto] p-8 md:p-12 gap-8">
        {/* Header */}
        <header className="flex items-center justify-between">
          <CinemaBrand size="lg" stacked={false} />
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">Programm · Nächste 7 Tage</div>
            <div className="font-mono text-2xl md:text-4xl tabular-nums text-[#F5F0E6] mt-1">
              {now.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
        </header>

        {/* Main slide */}
        <main className="grid md:grid-cols-[minmax(0,340px)_1fr] xl:grid-cols-[minmax(0,420px)_1fr] gap-10 md:gap-16 items-center">
          {/* Poster */}
          <div key={s.id + "-poster"} className="relative animate-fade-in">
            {m.poster_url ? (
              <div className="relative rounded-lg overflow-hidden border-2 border-[#D4AF37]/40 shadow-[0_0_60px_rgba(212,175,55,0.35)]">
                <img src={m.poster_url} alt={m.title_de} onError={(e) => { e.currentTarget.style.display = "none"; }}
                  className="w-full aspect-[2/3] object-cover" />
              </div>
            ) : (
              <div className="w-full aspect-[2/3] bg-[#1A1A1A] rounded-lg border-2 border-[#D4AF37]/40 flex items-center justify-center text-[#A8A398]">Kein Poster</div>
            )}
            {ev && ev.icon && (
              <div className="absolute -top-3 -right-3 rounded-full bg-black border border-[#D4AF37] px-3 py-1 text-[10px] uppercase tracking-widest flex items-center gap-1"
                style={{ color: ev.color }}>
                <span>{ev.icon}</span><span>{ev.l}</span>
              </div>
            )}
          </div>

          {/* Details */}
          <div key={s.id + "-info"} className="animate-fade-in-up space-y-5 md:space-y-6 min-w-0">
            <div>
              <div className="text-[10px] uppercase tracking-[0.5em] text-[#D4AF37] flex items-center gap-3">
                <CalendarIcon size={12} />
                {daysAway === 0 ? "Heute" : daysAway === 1 ? "Morgen" : `In ${daysAway} Tagen`}
                <span className="text-[#575148]">·</span>
                <span className="text-[#A8A398]">{dateLabel}</span>
              </div>
              <h1 className="font-heading text-5xl md:text-7xl xl:text-8xl leading-[0.95] mt-3 text-[#F5F0E6] break-words">
                {m.title_de || m.title_original}
              </h1>
              {m.title_original && m.title_de && m.title_original !== m.title_de && (
                <div className="italic text-[#A8A398] text-lg md:text-xl mt-2">{m.title_original}</div>
              )}
            </div>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#F5F0E6]/90">
              <div className="flex items-center gap-2 text-2xl font-mono tabular-nums text-[#D4AF37]">
                <Clock size={20} /> {s.start_time}
              </div>
              <Sep />
              <div className="flex items-center gap-2"><MapPin size={16} className="text-[#D4AF37]" /> {s.hall}</div>
              {m.duration_min > 0 && (<><Sep /><div>{m.duration_min} min</div></>)}
              {m.age_rating && (<><Sep /><div className="border border-[#D4AF37]/60 rounded px-2 py-0.5 text-[11px] font-mono">FSK {m.age_rating}</div></>)}
              {s.format && (<><Sep /><div className="text-[#A8A398]">{s.format}</div></>)}
              {s.language && (<><Sep /><div className="text-[#A8A398]">{s.language}{s.subtitles ? ` · UT ${s.subtitles}` : ""}</div></>)}
            </div>

            {/* Description */}
            {m.description && (
              <p className="text-[#D8D2C4]/90 text-base md:text-lg leading-relaxed max-w-3xl line-clamp-5">
                {m.description}
              </p>
            )}

            {/* Cast/Director */}
            <div className="text-xs md:text-sm text-[#A8A398] space-y-1 max-w-3xl">
              {m.director && <div><span className="text-[#575148] uppercase tracking-widest text-[10px] mr-2">Regie</span>{m.director}</div>}
              {m.cast && m.cast.length > 0 && <div><span className="text-[#575148] uppercase tracking-widest text-[10px] mr-2">Besetzung</span>{m.cast.slice(0, 5).join(" · ")}</div>}
            </div>

            {/* CTA */}
            <div className="flex items-center gap-4 pt-2">
              <div className="inline-flex items-center gap-3 bg-[#D4AF37] text-black px-6 py-3 rounded-md shadow-[0_10px_30px_rgba(212,175,55,0.35)]">
                <Ticket size={18} />
                <span className="uppercase tracking-widest font-semibold">Reservieren</span>
                <span className="font-mono text-lg">{s.ticket_price?.toFixed(2)} €</span>
              </div>
              {s.welcome_text && (
                <div className="italic text-[#EEDC82]/80 text-sm md:text-base max-w-md line-clamp-2">&bdquo;{s.welcome_text}&ldquo;</div>
              )}
            </div>
          </div>
        </main>

        {/* Rail: next slides preview */}
        <footer className="border-t border-[#1A1A1A]/70 pt-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398]">Als Nächstes</div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#575148]">
              {(idx % shows.length) + 1} / {shows.length}
            </div>
          </div>
          <div className="flex gap-3 overflow-hidden">
            {shows.map((it, i) => {
              const active = i === (idx % shows.length);
              return (
                <div key={it.id}
                  className={`flex-1 min-w-[9rem] flex items-center gap-3 rounded border transitions-safe px-3 py-2 ${
                    active ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#1A1A1A] bg-black/40"
                  }`}>
                  {it.movie?.poster_url ? (
                    <img src={it.movie.poster_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }}
                      className="w-8 h-12 object-cover rounded shrink-0" />
                  ) : <div className="w-8 h-12 bg-[#1A1A1A] rounded shrink-0" />}
                  <div className="min-w-0">
                    <div className={`text-[11px] truncate ${active ? "text-[#D4AF37]" : "text-[#F5F0E6]"}`}>{it.movie?.title_de || it.movie?.title_original}</div>
                    <div className="text-[10px] font-mono text-[#A8A398]">{shortDate(it.date)} · {it.start_time}</div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Progress */}
          <div className="mt-3 h-[2px] bg-[#1A1A1A] rounded overflow-hidden">
            <div key={s.id} className="h-full bg-[#D4AF37] animate-progress-12" />
          </div>
        </footer>
      </div>

      <style>{`
        @keyframes slowZoom { 0% { transform: scale(1) } 100% { transform: scale(1.08) } }
        .animate-slow-zoom { animation: slowZoom 12s linear both; }
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        .animate-fade-in { animation: fadeIn 800ms ease-out both; }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(18px) } to { opacity: 1; transform: none } }
        .animate-fade-in-up { animation: fadeInUp 900ms cubic-bezier(.2,.7,.2,1) both; }
        @keyframes progress { from { width: 0% } to { width: 100% } }
        .animate-progress-12 { animation: progress 12s linear both; }
      `}</style>
    </div>
  );
}

function Sep() {
  return <span className="w-1 h-1 rounded-full bg-[#D4AF37]/60" />;
}
function fmtDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso; }
}
function shortDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });
  } catch { return iso; }
}
